/**
 * rich_description_controller.js — the quote/job/invoice description editor
 * (Stimulus).
 *
 * Backs macros/rich_description.html, which is rendered by every surface that
 * can write a description: the inline click-to-edit block on detail, and the
 * shared edit modals. One controller for all of them, so the capabilities
 * cannot diverge — a surface that posted plain text instead would clear
 * description_html and drop the inline images with it.
 *
 * This controller owns ONLY the editor: content, formatting, images. The
 * surrounding form owns submission. The seam between them is a hidden
 * textarea[name="description_html"] that this controller mirrors on every
 * change, so an ordinary FormData serialization picks the markup up with no
 * cooperation from the host — inline_edit_controller and the modals' submit
 * paths never touch the editor.
 *
 * The extension set is deliberately narrower than Tiptap's defaults: it matches
 * the sanitiser's allowlist (application/utils/html_sanitizer.py) exactly.
 * Anything it could emit that the allowlist drops would vanish on save with no
 * explanation, so the formatting is not offered in the first place.
 *
 * Images are ordinary Attachment rows created through /api/v1/attachments — the
 * same rows the Photos card renders. That is what makes the description and the
 * gallery one image surface rather than two: uploading here adds to the
 * gallery, and "Choose existing" offers the gallery's photos back for inline
 * reuse. An image uploaded and then cancelled stays attached to the entity,
 * which is the honest outcome: the photo really was added to the job.
 *
 * Camera, Library, paste and drag-drop all funnel into _uploadAndInsert, so
 * there is one upload path and no way for a pasted photo to end up as some
 * other kind of image the Photos card knows nothing about.
 *
 * A photo is inserted only when its upload answers, and a camera photo on a
 * phone takes seconds. Until then it is in neither the editor nor the mirrored
 * markup, so a save in that window would post the description without it and
 * the photo would sit in the gallery alone. The editor therefore says it is
 * uploading (chippieUploading, aria-busy and the toolbar's "Uploading photo"
 * note) and hands out a promise of its uploads (chippieUploadsSettled), which
 * TurboEditModal.submit awaits before it reads the form.
 *
 * A form that creates the record (the macro's holds_images) has no entity to
 * upload to yet, so there the editor HOLDS each photo instead: it shows it
 * from the device (a blob: URL), and the mirrored markup carries
 * src="held-image:N" while the macro's description_images file input carries
 * the files, N being a file's position there. The form's ordinary multipart
 * post sends both and the server stores the photos and points the
 * placeholders at them (RichDescriptionService.with_held_images). Only photos
 * still in the description are posted: one deleted before saving is not.
 *
 * Images resize by dragging any of their four corners. The width is stored as a
 * percentage of the field, because the same markup renders into four containers
 * of different widths and only a proportion survives the trip.
 *
 * Config (data-* on the controller root):
 *   data-rich-description-entity-type  'quote' | 'job' | 'invoice'
 *   data-rich-description-entity-id    owning entity (attachment ownership)
 *   data-rich-description-editable-id  id to stamp on the editable element, so
 *                                      the mic's data-target and the
 *                                      AI-enhance include resolve to it
 *
 * entity-id is read at call time, not on connect: the shared edit modals render
 * once per page and are populated per row when opened, so the id arrives after
 * this controller has already connected.
 *
 * DOM-level API for hosts (no Stimulus outlet plumbing needed):
 *   element.chippieSetHTML(html)  replace the content (modal population, and
 *                                 the inline editor's Cancel)
 *   element.chippieGetHTML()      current markup, '' when empty
 *   element.chippieSetText(text)  replace the prose, keep the images (Chip
 *                                 fill, chip_fill_controller.js)
 *   element.chippieWhenReady()    a promise that resolves once the editor is
 *                                 mounted and chippieSetText writes (the
 *                                 walkthrough's fill). It stays pending while
 *                                 the editor is hidden or Tiptap fails to
 *                                 load, so a caller bounds its own wait
 *   element.chippieUploading()    true while a photo is uploading into it
 *   element.chippieUploadsSettled()  a promise that resolves once every
 *                                 upload in flight has answered and its photo
 *                                 is in the markup (never rejects)
 * and on the editable itself:
 *   chippieInsertText(text)       dictation's insertion seam
 *   chippieGetText()              prose only, one line per block
 *   chippieSetText(text)          replace the prose, keep the images
 *   chippieSetEditable(flag)      lock the editor while something writes to it
 * The last three are AI enhance's seam (ai_enhance.js). Enhance rewrites words,
 * not photos: it is handed the prose alone and its result is merged back with
 * every image still in place.
 *
 * Tiptap arrives asynchronously (esm/_tiptap.js), and only once the editor is
 * first on screen (_mountWhenShown), so the editor mounts a moment after the
 * inline form opens or the modal shows. Until then the hidden textarea is the
 * whole state: setHTML
 * writes to it and the editor loads its content from it on mount, so a modal
 * populated before the editor exists still opens with its text.
 *
 * An edit (typed, or written by Chip fill or AI enhance) fires `input` on the
 * hidden textarea once it is mirrored, so the host form reads the new value:
 * form-guard greys Create on it. A host's own writes (setHTML) fire nothing.
 * A reset of the surrounding form puts the editor back to the textarea's
 * reset value and lets go of any held photos.
 */

import { Controller } from "@hotwired/stimulus";
import { shrinkPhoto } from "./_photo_shrink.js";
import { loadTiptap } from "./_tiptap.js";

// Mirrors _DESCRIPTION_IMG_SRC in application/utils/html_sanitizer.py: the only
// img src the description sanitiser keeps.
const ATTACHMENT_SRC = /^\/api\/v1\/attachments\/\d+\/file$/;

// Mirrors _DESCRIPTION_IMG_STYLE in the same file: the only style it keeps.
// A percentage, never pixels — the one description renders into four containers
// of different widths (this editor, the public page, the PDF, the client's
// inbox), and only a proportion means the same thing in all four.
//
// Matched against the WHOLE attribute, because that is what the server does.
// Reading element.style.width instead would quietly rescue the width back out
// of "width:50%;position:fixed", which the server rejects outright — the editor
// would then be showing a size the save had already thrown away.
const IMAGE_STYLE = /^width:\s*(\d{1,3}(?:\.\d+)?)%\s*;?\s*$/;

// Below this a photo is a thumbnail nobody can see and the handle is hard to
// grab back; there is no reason to allow dragging past it.
const MIN_WIDTH_PCT = 10;

function clampPercent(percent) {
    return `${Math.min(100, Math.max(MIN_WIDTH_PCT, percent))}%`;
}

// The width a style attribute is allowed to set, or null for anything else.
// Applied to markup arriving by paste as well as by load, so a size that would
// be stripped on save is never shown as though it had taken.
function styleWidth(style) {
    const match = IMAGE_STYLE.exec(String(style || '').trim());
    return match ? clampPercent(parseFloat(match[1])) : null;
}

// A grip on every corner, because which corner is nearest depends on where the
// photo sits and which hand is holding the phone. `sign` maps horizontal drag to
// width: pull away from the picture's centre and it grows, so the left-hand
// grips count leftward movement as bigger. Height is never set (it follows from
// the aspect ratio), which is why top and bottom grips behave identically.
const RESIZE_CORNERS = [
    { name: 'nw', sign: -1 },
    { name: 'ne', sign: 1 },
    { name: 'sw', sign: -1 },
    { name: 'se', sign: 1 },
];

/**
 * Drag-to-resize node view for inline description images.
 *
 * Tiptap renders an image as a bare <img>, which has no corner to grab, so in
 * the editor the picture is wrapped in a box carrying the width and a grip on
 * each corner. The wrapper is an editor-only affordance: getHTML() serialises
 * through the extension's renderHTML, so what is stored, published and mailed
 * stays a plain <img style="width:N%">.
 */
function imageResizeView({ node, editor, getPos }) {
    const wrapper = document.createElement('div');
    wrapper.className = 'rd-image';
    const img = document.createElement('img');
    wrapper.append(img);

    let current = node;
    let releaseDrag = null;

    const paint = () => {
        img.src = current.attrs.src || '';
        img.alt = current.attrs.alt || '';
        if (current.attrs.title) img.title = current.attrs.title;
        else img.removeAttribute('title');
        // The width sits on the box rather than the picture because the handle
        // is positioned against the box: anchoring both to the same element is
        // what keeps the grip on the corner of the photo at every size.
        wrapper.style.width = current.attrs.width || '';
        img.style.width = current.attrs.width ? '100%' : '';
    };
    paint();

    const commit = (width) => {
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        if (pos == null) return;
        editor.view.dispatch(editor.view.state.tr.setNodeMarkup(
            pos, undefined, { ...current.attrs, width },
        ));
    };

    const startResize = (event, sign) => {
        // The image node is draggable, so without this the browser starts an
        // HTML5 drag of the photo the moment the handle moves.
        event.preventDefault();
        event.stopPropagation();

        const startX = event.clientX;
        const startWidth = img.getBoundingClientRect().width;
        const fieldWidth = editor.view.dom.clientWidth || startWidth;
        let latest = null;

        // Outlines the picture while it is being dragged, and pins the grips
        // visible: the pointer usually leaves the image during a resize, and
        // without this they would fade out mid-drag on the lost hover.
        wrapper.classList.add('rd-image--resizing');

        const onMove = (moveEvent) => {
            const pixels = startWidth + (moveEvent.clientX - startX) * sign;
            latest = clampPercent(Math.round((pixels / fieldWidth) * 100));
            // Previewed straight on the DOM rather than dispatched per move: a
            // transaction every frame would bury the undo history under a
            // hundred one-pixel steps, so the document learns the width once,
            // when the finger lifts.
            wrapper.style.width = latest;
            img.style.width = '100%';
        };
        const finish = () => {
            if (!releaseDrag) return;
            releaseDrag();
            releaseDrag = null;
            if (latest) commit(latest);
            else paint();
        };
        releaseDrag = () => {
            wrapper.classList.remove('rd-image--resizing');
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', finish);
            window.removeEventListener('pointercancel', finish);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', finish);
        window.addEventListener('pointercancel', finish);
    };
    // Built after startResize so each grip can close over its own direction.
    const handles = RESIZE_CORNERS.map(({ name, sign }) => {
        const el = document.createElement('span');
        el.className = `rd-image__handle rd-image__handle--${name}`;
        el.setAttribute('aria-hidden', 'true');
        el.draggable = false;
        const onDown = (event) => startResize(event, sign);
        el.addEventListener('pointerdown', onDown);
        wrapper.append(el);
        return { el, onDown };
    });

    return {
        dom: wrapper,
        update(updated) {
            if (updated.type.name !== 'image') return false;
            current = updated;
            paint();
            return true;
        },
        // An atom rendered by us: every mutation inside is our own preview
        // painting, and letting ProseMirror re-parse it would tear the node out
        // from under the drag in progress.
        ignoreMutation: () => true,
        destroy() {
            handles.forEach(({ el, onDown }) => el.removeEventListener('pointerdown', onDown));
            // A Turbo navigation mid-drag destroys the view before pointerup,
            // which would otherwise leave the move listeners on window forever.
            if (releaseDrag) releaseDrag();
        },
    };
}

// Built from the loaded module's Image rather than at import time, because
// Tiptap is not in this bundle (see the header).
function resizableImage(Image) {
    return Image.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                width: {
                    default: null,
                    parseHTML: (element) => styleWidth(element.getAttribute('style')),
                    renderHTML: (attributes) => (
                        attributes.width ? { style: `width:${attributes.width}` } : {}
                    ),
                },
            };
        },
        addNodeView() {
            return imageResizeView;
        },
    });
}

function csrfHeaders() {
    var token = (typeof FormValidator !== 'undefined' && FormValidator.getCsrfToken)
        ? FormValidator.getCsrfToken() : null;
    return token ? { 'X-CSRFToken': token } : {};
}

function notify(message, level) {
    if (typeof window.showNotification === 'function') {
        window.showNotification(message, level);
    }
}

export default class extends Controller {
        connect() {
            this.mount = this.element.querySelector('.rich-description-mount');
            this.textarea = this.element.querySelector('textarea');
            this.picker = this.element.querySelector('.rich-description-picker');
            this.editableId = this.element.dataset.richDescriptionEditableId;
            this.editor = null;
            this.heldImages = null;   // set only while an outside writer holds the field
            this.heldInput = this.element.querySelector('.rich-description-held');
            this.held = new Map();    // blob: URL -> File, for a record not yet saved
            this.uploadStatus = this.element.querySelector('.rich-description-uploading');
            this.uploads = new Set(); // one promise per batch of photos still uploading
            this._ready = new Promise((resolve) => { this._markReady = resolve; });

            this._mountWhenShown();

            this.element.chippieSetHTML = (html) => this.setHTML(html);
            this.element.chippieGetHTML = () => this.getHTML();
            this.element.chippieSetText = (text) => this._writeProse(text);
            this.element.chippieWhenReady = () => this._ready;
            this.element.chippieUploading = () => this.uploads.size > 0;
            this.element.chippieUploadsSettled = () => Promise.all(this.uploads);

            this.form = this.element.closest('form');
            // After the reset has put the textarea back, not before.
            this._onReset = () => setTimeout(() => this._afterReset());
            if (this.form) this.form.addEventListener('reset', this._onReset);
        }

        disconnect() {
            delete this.element.chippieSetHTML;
            delete this.element.chippieGetHTML;
            delete this.element.chippieSetText;
            delete this.element.chippieWhenReady;
            delete this.element.chippieUploading;
            delete this.element.chippieUploadsSettled;
            if (this.form) this.form.removeEventListener('reset', this._onReset);
            this._releaseHeld();
            if (this._visibility) {
                this._visibility.disconnect();
                this._visibility = null;
            }
            if (!this.editor) return;
            this.editor.destroy();
            this.editor = null;
            // destroy() leaves the mount node behind; empty it so a remount (or
            // a restored Turbo snapshot) starts from a clean element.
            if (this.mount) this.mount.innerHTML = '';
        }

        // --- Host API ---

        setHTML(html) {
            if (!this.editor) {
                if (this.textarea) this.textarea.value = html || '';
                return;
            }
            this.editor.commands.setContent(html || '');
            this._sync();
        }

        getHTML() {
            return this.textarea ? this.textarea.value : '';
        }

        // --- Formatting ---

        toggleBold() {
            if (this.editor) this.editor.chain().focus().toggleBold().run();
        }

        toggleItalic() {
            if (this.editor) this.editor.chain().focus().toggleItalic().run();
        }

        toggleBulletList() {
            if (this.editor) this.editor.chain().focus().toggleBulletList().run();
        }

        // --- Images ---

        // Fired by the hidden camera/library file inputs. Each file becomes an
        // Attachment on this entity (so it lands in the Photos card too) and the
        // row's stream URL is inserted inline — the only src shape the
        // description sanitiser keeps.
        async uploadImage(event) {
            const files = Array.from(event.target.files || []);
            event.target.value = '';   // allow re-picking the same file later
            await this._uploadAndInsert(files);
        }

        // Opens the strip of images already attached to this entity. Fetched on
        // every open rather than cached: the Photos card can have gained or lost
        // photos since this editor was mounted.
        async chooseExisting() {
            if (!this.picker) return;
            if (!this.picker.classList.contains('d-none')) {
                this._hidePicker();
                return;
            }
            if (!this._entityId()) {
                notify('Save this first, then add photos to the description.', 'error');
                return;
            }
            this.picker.innerHTML = '<span class="text-muted small">Loading photos…</span>';
            this.picker.classList.remove('d-none');
            try {
                const params = new URLSearchParams({
                    entity_type: this._entityType(),
                    entity_id: this._entityId(),
                    kind: 'image',
                });
                const resp = await fetch('/api/v1/attachments?' + params, {
                    credentials: 'same-origin',
                });
                if (!resp.ok) throw new Error('Could not load photos');
                this._renderPicker(await resp.json());
            } catch (err) {
                this._hidePicker();
                notify(err.message, 'error');
            }
        }

        insertExisting(event) {
            const thumb = event.target.closest('[data-attachment-url]');
            if (!thumb) return;
            this._insert({
                url: thumb.dataset.attachmentUrl,
                caption: thumb.dataset.attachmentAlt,
            });
            this._hidePicker();
        }

        // --- Internals ---

        _entityType() { return this.element.dataset.richDescriptionEntityType || ''; }
        _entityId() { return this.element.dataset.richDescriptionEntityId || ''; }

        // Every route an image can come in by ends here, so a pasted screenshot
        // and a Library pick produce the same Attachment row. One image surface
        // means one upload path.
        async _uploadAndInsert(files) {
            if (!files.length) return;
            if (!this._entityId()) {
                if (this.heldInput) files.forEach((file) => this._hold(file));
                else notify('Save this first, then add photos to the description.', 'error');
                return;
            }
            const batch = this._uploadEach(files);
            this.uploads.add(batch);
            this._showUploading();
            try {
                await batch;
            } finally {
                this.uploads.delete(batch);
                this._showUploading();
            }
        }

        // Never rejects: a failed photo is reported and the rest still go, so
        // a save waiting on this batch is never left hanging on an error.
        async _uploadEach(files) {
            for (const file of files) {
                try {
                    this._insert(await this._upload(file));
                } catch (err) {
                    notify(err.message || 'Upload failed', 'error');
                }
            }
        }

        _showUploading() {
            const uploading = this.uploads.size > 0;
            if (uploading) this.element.setAttribute('aria-busy', 'true');
            else this.element.removeAttribute('aria-busy');
            if (this.uploadStatus) this.uploadStatus.classList.toggle('d-none', !uploading);
        }

        // True when the paste/drop carried images and we have taken it over, so
        // ProseMirror leaves the event alone; false lets ordinary text and HTML
        // through untouched. The upload is deliberately not awaited — these
        // handlers must answer synchronously.
        _consumeImages(transfer) {
            const files = Array.from((transfer && transfer.files) || [])
                .filter((file) => file.type.startsWith('image/'));
            if (!files.length) return false;
            this._uploadAndInsert(files);
            return true;
        }

        // Parsed rather than regexed: DOMParser gives an inert document (no
        // scripts run, no images fetched) and cannot be fooled by an src
        // spelled across attributes the way a tag-shaped pattern can.
        _stripForeignImages(html) {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            doc.querySelectorAll('img').forEach((img) => {
                const path = this._ownAttachmentPath(img.getAttribute('src'));
                if (!path) {
                    img.remove();
                    return;
                }
                img.setAttribute('src', path);
                // Everything an outside page may have styled the picture with
                // goes, keeping only a width the sanitiser accepts back. Left
                // alone, a pasted `position:fixed` would render here and then
                // disappear on save with no explanation.
                const width = styleWidth(img.getAttribute('style'));
                if (width) img.setAttribute('style', `width:${width}`);
                else img.removeAttribute('style');
            });
            return doc.body.innerHTML;
        }

        // The attachment path for an image we own, or null for anything else.
        //
        // Resolved through URL rather than string-matched because copying
        // between two descriptions goes via the system clipboard, and the
        // browser absolutises src on the way out — the same photo comes back as
        // https://host/api/v1/attachments/42/file. The sanitiser only accepts
        // the path, so a same-origin absolute form is normalised back to it
        // instead of being thrown away. Resolving also settles the cases a
        // prefix test gets wrong: protocol-relative //evil.example and data:
        // both land on a foreign origin and are dropped.
        _ownAttachmentPath(src) {
            if (!src) return null;
            let url;
            try {
                url = new URL(src, window.location.origin);
            } catch (_e) {
                return null;
            }
            if (url.origin !== window.location.origin) return null;
            return ATTACHMENT_SRC.test(url.pathname) ? url.pathname : null;
        }

        // Every detail page renders this editor hidden (the inline form, the
        // edit modals), so mounting on connect fetched Tiptap on every visit.
        // A display:none element never intersects, so the observer fires when
        // the form opens or the modal shows, and not before.
        _mountWhenShown() {
            if (!('IntersectionObserver' in window)) {
                this._mountEditor();
                return;
            }
            this._visibility = new IntersectionObserver((entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                this._visibility.disconnect();
                this._visibility = null;
                this._mountEditor();
            });
            this._visibility.observe(this.element);
        }

        async _mountEditor() {
            if (this.editor || !this.mount) return;
            let tiptap;
            try {
                tiptap = await loadTiptap();
            } catch (_e) {
                notify('The description editor could not load. Check your connection and reload.', 'error');
                return;
            }
            // Disconnected (Turbo navigation, modal teardown) or mounted by a
            // second connect while the module was loading: nothing to do.
            if (!this.element.isConnected || this.editor) return;
            this._buildEditor(tiptap);
            this._markReady();
        }

        _buildEditor({ Editor, Image, StarterKit }) {
            this.editor = new Editor({
                element: this.mount,
                extensions: [
                    // Trimmed to the sanitiser's allowlist: no headings, code,
                    // strike, blockquote, rules or links. Disabling them also
                    // removes their input rules, so typing "# " or "> " no
                    // longer creates a node the save would silently strip.
                    StarterKit.configure({
                        heading: false,
                        code: false,
                        codeBlock: false,
                        strike: false,
                        blockquote: false,
                        horizontalRule: false,
                        link: false,
                    }),
                    resizableImage(Image),
                ],
                content: this.textarea ? this.textarea.value : '',
                editorProps: {
                    attributes: {
                        // Stamped on the ProseMirror element itself, not a
                        // wrapper: the shared box class keeps display and edit
                        // geometry identical, and the mic + AI-enhance
                        // selectors point at this id.
                        class: 'inline-edit-field',
                        id: this.editableId || '',
                    },
                    // Paste and drop are how an image arrives without touching
                    // the Camera/Library buttons — a screenshot, or a photo
                    // dragged off the desktop. Both go through the same upload
                    // as those buttons, so what lands inline is an ordinary
                    // Attachment row that the Photos card shows too.
                    handlePaste: (view, event) => this._consumeImages(event.clipboardData),
                    handleDrop: (view, event) => this._consumeImages(event.dataTransfer),
                    // An <img> copied off a web page carries an off-site src,
                    // which the sanitiser drops on save. Left alone it would
                    // show a photo that silently disappears when the tradie
                    // saves, so it is removed at the door instead — the same
                    // reason the extension set omits formatting the allowlist
                    // would strip. Attachment images (copied from another
                    // Chippie description) are already ours, so they stay.
                    transformPastedHTML: (html) => this._stripForeignImages(html),
                },
                onUpdate: () => {
                    this._sync();
                    this._announce();
                },
            });

            // Dictation writes through this hook instead of mutating the DOM:
            // ProseMirror re-parses any outside change and would detach the node
            // the transcript is being typed into. See dictation_controller.js.
            this.editor.view.dom.chippieInsertText = (text) => {
                this.editor.chain().focus().insertContent(text).run();
            };

            // AI enhance's seam. It streams plain prose, so it needs the prose
            // out and the prose back in — the images are this controller's
            // problem, not the enhancer's.
            const dom = this.editor.view.dom;
            dom.chippieGetText = () => this._readProse().lines.join('\n');
            dom.chippieSetText = (text) => this._writeProse(text);
            // Locking the editor is also the signal that an outside writer is
            // about to replace the prose repeatedly — enhance rewrites the whole
            // field on every streamed token. Snapshot where the images sit now,
            // while the user's own paragraphs are still there to measure
            // against: one token in there is no prose left, every image would
            // measure as "after 0 blocks", and they would all pile up at the top.
            dom.chippieSetEditable = (flag) => {
                this.heldImages = flag ? null : this._readProse().images;
                this.editor.setEditable(flag);
            };
        }

        // The document split into the two things enhance treats differently:
        // the prose it rewrites, and the images it must not disturb. Read in
        // ONE pass so both halves count blocks identically — an image recorded
        // as "after 2 blocks" against a different notion of block than the one
        // that produced the lines would come back in the wrong place.
        //
        // Descends to leaf textblocks, so each list item is its own line rather
        // than the whole list arriving as one run-on string. A hard break splits
        // a line too: the sanitiser counts <br> as a block boundary, and
        // textContent would flatten it away and hand the model two sentences
        // welded together.
        _readProse() {
            const lines = [];
            const images = [];
            if (!this.editor) return { lines, images };
            this.editor.state.doc.descendants((node) => {
                if (node.type.name === 'image') {
                    images.push({ attrs: node.attrs, after: lines.length });
                    return false;
                }
                if (!node.isTextblock) return true;
                node.textBetween(0, node.content.size, '\n',
                                 (leaf) => (leaf.type.name === 'hardBreak' ? '\n' : ''))
                    .split('\n')
                    .forEach((line) => {
                        const text = line.trim();
                        if (text) lines.push(text);
                    });
                return false;    // inline content is not a block
            });
            return { lines, images };
        }

        // Replace the prose with `text`, one paragraph per line, putting each
        // image back after the same number of paragraphs it followed before. An
        // image that sat after the second paragraph sits after the second
        // paragraph of the rewrite; images past the end of a shorter rewrite
        // fall to the end rather than being dropped.
        //
        // Built as a ProseMirror document rather than an HTML string: the text
        // is model output and would otherwise need escaping, and a doc node
        // cannot carry markup by accident.
        _writeProse(text) {
            if (!this.editor) return;
            // heldImages is set for the duration of an outside write sequence;
            // outside of one, the document itself is the truth.
            const images = this.heldImages || this._readProse().images;
            const lines = String(text || '').split('\n')
                .map((line) => line.trim()).filter(Boolean);

            const content = [];
            let placed = 0;
            const placeImagesAfter = (blockCount) => {
                while (placed < images.length && images[placed].after <= blockCount) {
                    content.push({ type: 'image', attrs: images[placed].attrs });
                    placed += 1;
                }
            };

            placeImagesAfter(0);                       // images that led the description
            lines.forEach((line, index) => {
                content.push({ type: 'paragraph', content: [{ type: 'text', text: line }] });
                placeImagesAfter(index + 1);
            });
            while (placed < images.length) {           // anchored past the new end
                content.push({ type: 'image', attrs: images[placed].attrs });
                placed += 1;
            }
            if (!content.length) content.push({ type: 'paragraph' });

            this.editor.commands.setContent({ type: 'doc', content });
            this._sync();
            this._announce();
        }

        // Tiptap renders an empty document as "<p></p>". Storing that would make
        // description_html truthy while projecting to no text at all, so an
        // empty editor mirrors an empty string and `required`-style checks in
        // the host see the field as blank.
        _sync() {
            if (!this.editor || !this.textarea) return;
            const html = this.editor.isEmpty ? '' : this.editor.getHTML();
            this.textarea.value = this.held.size ? this._placeHeld(html) : html;
        }

        _announce() {
            if (this.textarea) this.textarea.dispatchEvent(new Event('input', { bubbles: true }));
        }

        // Swap each held photo's blob: URL for its placeholder, and put exactly
        // the photos still in the description into the file input, in the
        // order the placeholders number them. A photo pasted twice is one file.
        _placeHeld(html) {
            const files = [];
            const positions = new Map();
            const placed = html.replace(/src="(blob:[^"]+)"/g, (whole, url) => {
                const file = this.held.get(url);
                if (!file) return whole;
                if (!positions.has(url)) {
                    positions.set(url, files.length);
                    files.push(file);
                }
                return `src="held-image:${positions.get(url)}"`;
            });
            const transfer = new DataTransfer();
            files.forEach((file) => transfer.items.add(file));
            this.heldInput.files = transfer.files;
            return placed;
        }

        // Shown at once from the original. Once a smaller copy is ready it
        // takes the original's place in what the form posts: a phone photo is
        // several megabytes, and the whole form is one request under nginx's
        // cap. A save tapped before then posts the original.
        _hold(file) {
            const url = URL.createObjectURL(file);
            this.held.set(url, file);
            this._insert({ url, original_filename: file.name });
            shrinkPhoto(file).then((small) => {
                if (small === file || this.held.get(url) !== file) return;
                this.held.set(url, small);
                this._sync();
            });
        }

        _releaseHeld() {
            this.held.forEach((_file, url) => URL.revokeObjectURL(url));
            this.held.clear();
        }

        _afterReset() {
            this._releaseHeld();
            this.setHTML(this.textarea ? this.textarea.value : '');
        }

        async _upload(file) {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('entity_type', this._entityType());
            fd.append('entity_id', this._entityId());
            fd.append('kind', 'image');
            const resp = await fetch('/api/v1/attachments', {
                method: 'POST', body: fd, headers: csrfHeaders(), credentials: 'same-origin',
            });
            if (!resp.ok) {
                let msg = 'Upload failed';
                try { msg = (await resp.json()).error || msg; } catch (_e) { /* keep default */ }
                throw new Error(msg);
            }
            return resp.json();
        }

        // An image just inserted is left selected, and inserting over a
        // selection replaces it, so the second of several photos picked at
        // once would overwrite the first. A selected image is kept and the new
        // one goes after it.
        _insert(row) {
            if (!this.editor || !row || !row.url) return;
            const image = {
                type: 'image',
                attrs: { src: row.url, alt: row.caption || row.original_filename || '' },
            };
            const chain = this.editor.chain().focus();
            if (this.editor.isActive('image')) {
                chain.insertContentAt(this.editor.state.selection.to, image).run();
            } else {
                chain.insertContent(image).run();
            }
        }

        _renderPicker(rows) {
            if (!rows.length) {
                this.picker.textContent =
                    'No photos on this ' + this._entityType() +
                    ' yet. Use Camera or Library to add one.';
                this.picker.className = 'rich-description-picker text-muted small';
                return;
            }
            this.picker.className = 'rich-description-picker';
            this.picker.replaceChildren(...rows.map((row) => {
                const alt = row.caption || row.original_filename || '';
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'rich-description-picker__thumb';
                btn.title = alt;
                btn.dataset.attachmentUrl = row.url;
                btn.dataset.attachmentAlt = alt;
                // The tile shows the small copy; picking inserts row.url, the
                // original, which the description, PDF and public view render.
                const img = document.createElement('img');
                img.src = row.thumb_url;
                img.alt = '';
                img.loading = 'lazy';
                btn.appendChild(img);
                return btn;
            }));
        }

        _hidePicker() {
            if (this.picker) this.picker.classList.add('d-none');
        }
}
